import { useEffect, useMemo } from 'react'

import { useTranslation } from 'react-i18n-lite'

import {
  sendMessageToParent,
  useDisplayUserError
} from '@yes.technology/react-toolkit'

import { useAppDispatch, useAppSelector } from 'state/store'

import { InteractionModel, Step } from 'types/shared'

import { validateStepCallback } from '../../../../utils/interactionModel'
import {
  interactionModelOperations,
  loadingOperations,
  goToNextStep,
  goToPreviousStep,
  goToLastStep,
  REVIEW_STEP_UUID
} from '../../redux'
import { getStepErrorMessages, showError } from '../../utils/validation'
import useValidateStep from '../useValidateStep/useValidateStep'
import { flatValuesToOldActivities } from '../../utils/flatValuesToOldActivities'
import useSequenceRules from './useSequenceRules'
import useDecisionModelAutomation from './useDecisionModelAutomation'
import useWorkflow from '../useWorkflow'
import useSaveDraftInteraction from '../useSaveDraftInteraction'
import * as googleTagManager from 'modules/shared/GTM'
import { useSearchParams } from 'react-router-dom'
import useInterpolateUrl from '../useInterpolateUrl'

import useSaveAndFinish from './useSaveAndFinish'
import { goToFirstStep } from 'modules/interaction/redux/steps'

type UseStepParams = {
  interactionModel: InteractionModel
  onSave: () => void
  displayedSteps?: Step[]
  currentStepUuid?: string
}

const useStep = ({
  interactionModel,
  onSave,
  displayedSteps = [],
  currentStepUuid = ''
}: UseStepParams) => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const interpolateUrl = useInterpolateUrl()

  const isLoadedSteps = !!displayedSteps?.length

  const activityModels = useMemo(
    () => interactionModel.flatData?.activity_models || {},
    [interactionModel.flatData?.activity_models]
  )

  const activityValues = useMemo(
    () => interactionModel.flatData?.activity_values || {},
    [interactionModel.flatData?.activity_values]
  )

  const { hasPendingDecisionModels } = useSequenceRules({
    activityModels,
    activityValues
  })

  const { workflow: process } = useWorkflow({
    workflowUuid: interactionModel.workflowUuid,
    t
  })

  const {
    hasPendingDecisionModels: hasPendingDMNDecisionModels,
    executeDecisionModelAutomation
  } = useDecisionModelAutomation({
    decisionModels: interactionModel.flatData?.decision_models ?? [],
    activityValues,
    process
  })

  useEffect(() => {
    const automations = interactionModel.flatData?.automations ?? []
    automations.forEach((automation) => {
      if (
        automation.automation_type === 'decision-model-action' &&
        automation.trigger === 'activity-value-change'
      ) {
        executeDecisionModelAutomation(automation)
      }
    })
  }, [
    activityValues,
    interactionModel.flatData?.automations,
    executeDecisionModelAutomation
  ])

  const allHasPendingDecisionModels =
    hasPendingDecisionModels || hasPendingDMNDecisionModels

  const {
    saveAndFinishAfterDraftUpdates,
    isExecutingAutomations,
    uuidInteraction,
    isInteractionFinished
  } = useSaveAndFinish(interactionModel, onSave, displayedSteps)

  useEffect(() => {
    if (
      isInteractionFinished &&
      !interactionModel?.isFetching &&
      uuidInteraction &&
      !isExecutingAutomations
    ) {
      const informationCards = interactionModel.flatData?.information_cards
      const hasConclusionCard = informationCards?.some(
        (card) => card.display_on === 'interaction-finished'
      )

      if (!hasConclusionCard) {
        sendMessageToParent(
          interactionModel.workflowUuid || '',
          uuidInteraction
        )
      }
    }
  }, [
    isInteractionFinished,
    interactionModel,
    uuidInteraction,
    isExecutingAutomations
  ])

  const { displayUserError } = useDisplayUserError()

  const { validateStep } = useValidateStep()

  const [searchParams] = useSearchParams()
  const draftInteractionUuid = searchParams.get('draft_interaction_uuid') || ''

  const { createDraftInteraction, updateDraftInteraction } =
    useSaveDraftInteraction()

  const handleNextStep = async () => {
    const currentStep = displayedSteps.find((s) => s.uuid === currentStepUuid)

    if (!currentStep) return

    const { invalidActivities, updatedActivityValues } = await validateStep({
      activityModels,
      activityValues,
      stepActivities: currentStep.activity_models
    })

    dispatch(
      interactionModelOperations.updateActivities(
        flatValuesToOldActivities(updatedActivityValues)
      )
    )

    getStepErrorMessages({
      invalidActivities,
      stepName: currentStep.des,
      t
    }).forEach((error) => showError({ error }))

    if (invalidActivities.length > 0) return

    if (currentStep?.validation_step_callback) {
      const interpolatedCallback = interpolateUrl(
        currentStep.validation_step_callback
      )

      dispatch(
        loadingOperations.startLoading({
          des: t('step.validation-loading.des'),
          complement: t('step.validation-loading.complement')
        })
      )

      const responseValidationCallback = await validateStepCallback({
        activityValues,
        stepUuid: currentStep.uuid,
        endpoint: interpolatedCallback
      })

      dispatch(loadingOperations.stopLoading())

      if (!responseValidationCallback?.success) {
        displayUserError({ error: responseValidationCallback })
        return
      }
    }

    if (draftInteractionUuid) updateDraftInteraction({ interactionModel })
    else createDraftInteraction({ interactionModel })

    googleTagManager.setDataLayer({
      event: googleTagManager.Events.INTERACTION_PROGRESS
    })

    dispatch(goToNextStep())
    window.scrollTo(0, 0)
  }

  const handlePreviousStep = () => {
    dispatch(goToPreviousStep())
    window.scrollTo(0, 0)
  }

  const handleSaveAndFinish = () => {
    googleTagManager.setDataLayer({
      event: googleTagManager.Events.INTERACTION_PROGRESS,
      clicked_button: 'save_and_finish'
    })

    saveAndFinishAfterDraftUpdates()
  }

  const { informational: informationalInteraction } = useAppSelector(
    (state) => state.interactionModel
  )

  const additionalSteps = informationalInteraction ? 0 : 1
  const totalSteps = displayedSteps.length + additionalSteps

  const currentStepIndex =
    currentStepUuid === REVIEW_STEP_UUID
      ? totalSteps
      : displayedSteps.findIndex((s) => s.uuid === currentStepUuid) + 1 || 1

  const navigationActions = {
    first: () => {
      dispatch(goToFirstStep())
    },
    previous: () => {
      dispatch(goToPreviousStep())
    },
    next: () => {
      handleNextStep()
    },
    last: () => {
      dispatch(goToLastStep())
    }
  }

  const getActionByPosition = (position: number) => {
    if (position === currentStepIndex + 1) return navigationActions.next
    if (position === currentStepIndex - 1) return navigationActions.previous
    if (position === 1) return navigationActions.first
    if (position === totalSteps) return navigationActions.last
    return null
  }

  const changeStep = (position: number) => {
    const action = getActionByPosition(position)

    action?.()

    window.scrollTo(0, 0)
  }

  return {
    steps: displayedSteps,
    totalSteps,
    currentStepIndex,
    uuidInteraction,
    handleNextStep,
    handlePreviousStep,
    handleSaveAndFinish,
    changeStep,
    isLoadedSteps,
    isInteractionFinished,
    hasPendingDecisionModels: allHasPendingDecisionModels
  }
}

export { REVIEW_STEP_UUID }
export default useStep
